<template>
  <div class="resident-list">
    <section class="list-header">
      <div class="title-row">
        <v-btn icon class="back-button" @click="goHome">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <h1>{{ title }}</h1>
      </div>
      <div class="header-actions">
        <v-btn v-if="isDemands" depressed color="primary" class="create-button" @click="openCreateDemand">
          <v-icon left small>mdi-plus</v-icon>
          Novo
        </v-btn>
        <v-btn icon class="refresh-button" @click="load">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </section>

    <section v-if="!items.length" class="empty-panel">
      <v-icon color="primary" size="36">{{ isDemands ? "mdi-clipboard-plus-outline" : "mdi-leaf-circle-outline" }}</v-icon>
      <strong>Nenhum registro por enquanto</strong>
      <span>{{ isDemands ? "Abra um chamado para enviar uma solicitação à gestão do local." : "As informações do seu local aparecerão aqui." }}</span>
      <v-btn v-if="isDemands" depressed color="primary" class="empty-action" @click="openCreateDemand">
        Criar chamado
      </v-btn>
    </section>

    <article v-for="item in items" :key="item.id" class="list-card">
      <div class="list-icon"><v-icon color="primary">{{ icon }}</v-icon></div>
      <div>
        <strong>{{ itemTitle(item) }}</strong>
        <span>{{ itemSummary(item) }}</span>
      </div>
      <div class="list-actions">
        <v-chip x-small color="secondary" text-color="primary">{{ statusLabel(item.status) }}</v-chip>
        <v-btn small text color="primary" class="details-button" @click="openDetails(item)">
          Detalhes
          <v-icon right small>mdi-chevron-up</v-icon>
        </v-btn>
      </div>
    </article>

    <v-bottom-sheet v-model="detailsSheet" inset>
      <v-card v-if="selectedItem" class="detail-sheet" :class="{ 'demand-detail-sheet': isDemands }">
        <div class="detail-handle" />
        <div class="detail-header">
          <div class="list-icon"><v-icon color="primary">{{ icon }}</v-icon></div>
          <div>
            <span>{{ title }}</span>
            <strong>{{ itemTitle(selectedItem) }}</strong>
          </div>
          <v-btn icon class="close-button" @click="detailsSheet = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-chip x-small color="secondary" text-color="primary" class="detail-status">
          {{ statusLabel(selectedItem.status) }}
        </v-chip>

        <p v-if="selectedItem.description || selectedItem.content" class="detail-description">
          {{ selectedItem.description || selectedItem.content }}
        </p>

        <div class="detail-list">
          <div v-for="row in detailRows(selectedItem)" :key="row.label" class="detail-row">
            <span>{{ row.label }}</span>
            <strong>{{ row.value }}</strong>
          </div>
        </div>

        <section v-if="isDemands" class="demand-thread">
          <div class="thread-heading">
            <strong>Conversa do chamado</strong>
            <span>Acompanhe as respostas da gestão e envie novas informações quando necessário.</span>
          </div>

          <div ref="demandThreadList" class="thread-list">
            <div v-for="comment in demandComments" :key="comment.id" class="thread-item">
              <div class="thread-avatar">{{ initials(comment.user && comment.user.name) }}</div>
              <div class="thread-content">
                <div class="thread-meta">
                  <strong>{{ (comment.user && comment.user.name) || "Usuário" }}</strong>
                  <span>{{ formatDateTime(comment.created_at) }}</span>
                </div>
                <p v-if="comment.message">{{ comment.message }}</p>
                <a v-if="comment.resource" class="thread-attachment" :href="resourceUrl(comment.resource)" target="_blank" rel="noopener">
                  <v-icon size="17">mdi-paperclip</v-icon>
                  {{ comment.resource.name || "Anexo" }}
                </a>
              </div>
            </div>

            <div v-if="!demandComments.length" class="thread-empty">
              Nenhuma resposta registrada ainda.
            </div>
          </div>

          <div class="demand-reply-footer">
            <v-textarea
              v-model="demandReply"
              label="Responder chamado"
              placeholder="Envie uma atualização ou complemento para a gestão."
              outlined
              rows="2"
              hide-details="auto"
            />
            <v-btn
              block
              depressed
              color="primary"
              class="reply-button"
              :loading="sendingDemandReply"
              :disabled="!canSendDemandReply"
              @click="sendDemandReply"
            >
              Enviar resposta
              <v-icon right small>mdi-send-outline</v-icon>
            </v-btn>
          </div>
        </section>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="createDemandSheet" inset>
      <v-card class="detail-sheet demand-form-sheet">
        <div class="detail-handle" />
        <div class="detail-header">
          <div class="list-icon"><v-icon color="primary">mdi-clipboard-plus-outline</v-icon></div>
          <div>
            <span>Chamados</span>
            <strong>Novo chamado</strong>
          </div>
          <v-btn icon class="close-button" @click="createDemandSheet = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-form ref="demandFormRef" v-model="demandFormValid" lazy-validation class="demand-form" @submit.prevent="createDemand">
          <v-select
            v-model="demandForm.category"
            :items="demandCategories"
            :rules="[$rules.required]"
            label="Categoria"
            outlined
            hide-details="auto"
          />

          <v-text-field
            v-model="demandForm.title"
            :rules="[$rules.required]"
            label="Título"
            placeholder="Ex.: Iluminação da rua"
            outlined
            hide-details="auto"
          />

          <v-textarea
            v-model="demandForm.description"
            :rules="[$rules.required]"
            label="Descrição"
            placeholder="Descreva o que precisa ser verificado pela gestão."
            outlined
            rows="4"
            hide-details="auto"
          />

          <div class="address-preview">
            <v-icon color="primary">mdi-map-marker-outline</v-icon>
            <span>{{ demandAddressText }}</span>
          </div>

          <v-btn block depressed color="primary" class="save-demand-button" :loading="savingDemand" type="submit">
            Criar chamado
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
        </v-form>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "BoaruaResidentResourceList",
  data() {
    return {
      detailsSheet: false,
      createDemandSheet: false,
      demandFormValid: true,
      savingDemand: false,
      sendingDemandReply: false,
      items: [],
      selectedItem: null,
      demandComments: [],
      demandReply: "",
      demandForm: {
        category: "maintenance",
        title: "",
        description: "",
      },
      demandCategories: [
        { text: "Manutenção", value: "maintenance" },
        { text: "Limpeza", value: "cleaning" },
        { text: "Iluminação", value: "lighting" },
        { text: "Segurança", value: "security" },
        { text: "Financeiro", value: "financial" },
        { text: "Outro", value: "other" },
      ],
    };
  },
  computed: {
    title() {
      return this.$route.meta.title;
    },
    endpoint() {
      return this.$route.meta.endpoint;
    },
    fixedPostTypes() {
      return this.$route.meta.fixedPostTypes || [];
    },
    isDemands() {
      return this.$route.path.includes("demandas");
    },
    isFinancial() {
      return this.$route.path.includes("financeiro");
    },
    profile() {
      return JSON.parse(localStorage.getItem("associationProfile") || "{}");
    },
    primaryUnit() {
      return this.profile.units && this.profile.units.length ? this.profile.units[0] : null;
    },
    demandAddressText() {
      return this.addressFromUnit(this.primaryUnit) || this.addressFromProfile() || "Endereço da residência vinculada";
    },
    icon() {
      const path = this.$route.path;
      if (path.includes("financeiro")) return "mdi-barcode";
      if (path.includes("assembleias")) return "mdi-account-voice";
      if (path.includes("feed")) return "mdi-newspaper-variant-outline";
      return "mdi-clipboard-text-outline";
    },
    canSendDemandReply() {
      return Boolean(String(this.demandReply || "").trim() && this.selectedItem && this.selectedItem.id);
    },
  },
  watch: {
    "$route.path": "load",
    "$route.query.demand_id": "openDemandFromQuery",
  },
  mounted() {
    this.load();
  },
  methods: {
    goHome() {
      if (this.$route.path !== "/home") {
        this.$router.push("/home");
      }
    },
    async load() {
      const profile = this.profile;
      const baseParams = { tenant_id: profile.tenant_id, limit: 30 };

      if (this.fixedPostTypes.length) {
        const responses = await Promise.all(
          this.fixedPostTypes.map((postType) => axios.get(this.endpoint, { params: { ...baseParams, post_type: postType } }))
        );
        this.items = responses
          .flatMap((response) => response.data.content.data || response.data.content || [])
          .filter((item) => this.fixedPostTypes.includes(item.post_type));
        return;
      }

      const response = await axios.get(this.endpoint, { params: baseParams });
      this.items = response.data.content.data || response.data.content || [];
      this.openDemandFromQuery();
    },
    openCreateDemand() {
      this.resetDemandForm();
      this.createDemandSheet = true;
      this.$nextTick(() => {
        if (this.$refs.demandFormRef && this.$refs.demandFormRef.resetValidation) {
          this.$refs.demandFormRef.resetValidation();
        }
      });
    },
    resetDemandForm() {
      this.demandForm = {
        category: "maintenance",
        title: "",
        description: "",
      };
    },
    async createDemand() {
      if (this.$refs.demandFormRef && !this.$refs.demandFormRef.validate()) return;

      const profile = this.profile;
      const unit = this.primaryUnit;
      this.savingDemand = true;

      try {
        await axios.post("/demands", {
          tenant_id: profile.tenant_id,
          association_id: profile.association_id,
          member_id: profile.id,
          unit_id: unit ? unit.id : null,
          title: this.demandForm.title,
          category: this.demandForm.category,
          description: this.demandForm.description,
          status: "open",
          street: unit ? unit.street : profile.street,
          number: unit ? unit.number : profile.number,
          latitude: unit ? unit.latitude : profile.latitude,
          longitude: unit ? unit.longitude : profile.longitude,
        });
        this.createDemandSheet = false;
        this.resetDemandForm();
        await this.load();
        if (this.$dialog && this.$dialog.notify) {
          this.$dialog.notify.success("Chamado criado com sucesso", { position: "top-right", timeout: 3000 });
        }
      } catch (error) {
        if (this.$dialog && this.$dialog.notify) {
          this.$dialog.notify.error(this.$codes ? this.$codes(error) : "Não foi possível criar o chamado", { position: "top-right", timeout: 5000 });
        }
      } finally {
        this.savingDemand = false;
      }
    },
    async openDetails(item) {
      this.selectedItem = item;
      this.demandComments = [];
      this.demandReply = "";
      this.detailsSheet = true;

      if (!item || !item.id) return;

      try {
        const response = await axios.get(`${this.endpoint}/${item.id}`);
        const result = response.data.content || item;
        this.selectedItem = result;
        this.demandComments = this.normalizeComments(result.comments || []);
        if (this.isDemands) {
          await this.loadDemandComments(item.id);
        }
      } catch (error) {
        this.selectedItem = item;
        if (this.isDemands) {
          await this.loadDemandComments(item.id);
        }
      }
    },
    openDemandFromQuery() {
      if (!this.isDemands || !this.$route.query.demand_id) return;
      const demandId = Number(this.$route.query.demand_id);
      if (this.selectedItem && Number(this.selectedItem.id) === demandId && this.detailsSheet) return;
      const demand = this.items.find((item) => Number(item.id) === demandId);
      if (demand) this.openDetails(demand);
    },
    normalizeComments(comments) {
      if (comments && Array.isArray(comments.rows)) return comments.rows;
      if (comments && Array.isArray(comments.data)) return comments.data;
      return Array.isArray(comments) ? comments : [];
    },
    async loadDemandComments(demandId) {
      if (!demandId) return;
      try {
        const response = await axios.get(`${this.endpoint}/${demandId}/comments`);
        const content = response.data.content || [];
        this.demandComments = this.normalizeComments(content).filter(Boolean);
        this.$nextTick(this.scrollDemandThreadToEnd);
      } catch (error) {
        if (!this.demandComments.length) this.demandComments = [];
      }
    },
    scrollDemandThreadToEnd() {
      const el = this.$refs.demandThreadList;
      if (!el || typeof el.scrollTo !== "function") return;
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    },
    async sendDemandReply() {
      if (!this.canSendDemandReply) return;

      this.sendingDemandReply = true;
      try {
        await axios.post(`${this.endpoint}/${this.selectedItem.id}/comments`, {
          message: String(this.demandReply || "").trim(),
        });
        this.demandReply = "";
        await this.loadDemandComments(this.selectedItem.id);
        if (this.$dialog && this.$dialog.notify) {
          this.$dialog.notify.success("Resposta enviada", { position: "top-right", timeout: 3000 });
        }
      } catch (error) {
        if (this.$dialog && this.$dialog.notify) {
          this.$dialog.notify.error(this.$codes ? this.$codes(error) : "Não foi possível enviar a resposta", { position: "top-right", timeout: 5000 });
        }
      } finally {
        this.sendingDemandReply = false;
      }
    },
    itemTitle(item) {
      if (!item) return "Registro";
      return item.title || item.name || this.formatReferenceMonth(item.reference_month) || "Registro";
    },
    itemSummary(item) {
      if (!item) return "Sem detalhes adicionais";
      if (this.isFinancial) {
        return this.paymentOriginText(item);
      }
      if (this.$route.path.includes("assembleias")) {
        const date = this.formatDateTime(item.starts_at);
        const place = item.location_name || item.location_address;
        return [date, place].filter(Boolean).join(" - ") || "Assembleia cadastrada";
      }
      return item.description || item.content || item.local || item.location_name || "Sem detalhes adicionais";
    },
    addressFromUnit(unit) {
      if (!unit) return "";
      return [unit.street, unit.number, unit.complement].filter(Boolean).join(", ");
    },
    addressFromProfile() {
      return [this.profile.street, this.profile.number, this.profile.complement].filter(Boolean).join(", ");
    },
    detailRows(item) {
      if (!item) return [];
      const rows = [];

      if (item.starts_at) rows.push({ label: "Início", value: this.formatDateTime(item.starts_at) });
      if (item.ends_at) rows.push({ label: "Término", value: this.formatDateTime(item.ends_at) });
      if (item.due_date) rows.push({ label: "Vencimento", value: this.formatDate(item.due_date) });
      if (item.reference_month) rows.push({ label: "Referência", value: this.formatReferenceMonth(item.reference_month) });
      if (this.isFinancial) rows.push({ label: "Origem do pagamento", value: this.paymentOriginValue(item) });
      if (item.location_name) rows.push({ label: "Local", value: item.location_name });
      if (item.location_address) rows.push({ label: "Endereço", value: item.location_address });
      if (this.isDemands && item.priority) rows.push({ label: "Prioridade", value: this.priorityLabel(item.priority) });
      if (item.created_at) rows.push({ label: "Criado em", value: this.formatDateTime(item.created_at) });

      return rows.length ? rows : [{ label: "Detalhes", value: this.itemSummary(item) }];
    },
    statusLabel(status) {
      const labels = {
        active: "Ativo",
        inactive: "Inativo",
        scheduled: "Agendada",
        in_progress: "Em andamento",
        completed: "Concluída",
        cancelled: "Cancelada",
        pending: "Pendente",
        paid: "Pago",
        overdue: "Em atraso",
        open: "Aberto",
        in_review: "Em análise",
        waiting_resident: "Aguardando morador",
        resolved: "Resolvido",
        closed: "Fechado",
        published: "Publicado",
        draft: "Rascunho",
      };
      return labels[status] || status || "Ativo";
    },
    priorityLabel(priority) {
      const labels = {
        low: "Baixa",
        normal: "Normal",
        medium: "Média",
        high: "Alta",
        urgent: "Urgente",
      };
      return labels[priority] || priority || "-";
    },
    paymentOriginText(item = {}) {
      const origin = this.paymentOriginValue(item);
      return origin && origin !== "-" ? `Origem: ${origin}` : "Origem não informada";
    },
    paymentOriginValue(item = {}) {
      return item.payment_origin_label || this.paymentOriginLabel(item.payment_origin) || "-";
    },
    paymentOriginLabel(value = "") {
      const labels = {
        reconciliation: "Conciliação",
        system: "Sistema",
      };
      return labels[String(value || "").toLowerCase()] || "";
    },
    initials(name) {
      return String(name || "U")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase();
    },
    resourceUrl(resource = {}) {
      return resource.file_url || resource.url || "#";
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("pt-BR");
    },
    formatDateTime(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatReferenceMonth(value) {
      const match = String(value || "").match(/^(\d{4})-(\d{2})/);
      if (!match) return value || "";
      const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
      const month = date.toLocaleDateString("pt-BR", { month: "long" });
      return `${month.charAt(0).toUpperCase()}${month.slice(1)} de ${date.getFullYear()}`;
    },
  },
};
</script>

<style scoped lang="scss">
.resident-list {
  min-height: 100%;
  padding: 0 16px 18px;
  color: #111827;
}

.list-card,
.empty-panel {
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 16px;
}

.list-header {
  min-height: 72px;
  margin: 0 -16px 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-bottom: 1px solid #edf0ed;

  h1 {
    margin: 0;
    font-size: 24px;
  }
}

.title-row {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.back-button,
.refresh-button {
  background: #f5f7f5;
  border: 1px solid #dfe7df;
  border-radius: 12px;
}

.back-button {
  flex: 0 0 auto;
}

.header-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.create-button {
  min-height: 40px;
  padding: 0 12px !important;
  border-radius: 12px;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: none;
}

.empty-action {
  margin-top: 12px;
  border-radius: 12px;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: none;
}

.list-card {
  min-height: 86px;
  margin-bottom: 10px;
  padding: 13px 14px;
  display: flex;
  align-items: center;
  gap: 12px;

  > div:nth-child(2) {
    flex: 1;
    min-width: 0;
  }

  strong,
  span {
    display: block;
  }

  span {
    margin-top: 4px;
    color: #66736b;
    font-size: 13px;
  }
}

.list-actions {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.details-button {
  min-width: 0 !important;
  padding: 0 6px !important;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: none;
}

.list-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef7f1;
  border-radius: 12px;
}

.empty-panel {
  min-height: 220px;
  padding: 22px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  text-align: center;

  span {
    color: #66736b;
    font-size: 13px;
  }
}

.detail-sheet {
  padding: 10px 18px 22px;
  color: #111827;
  border-radius: 24px 24px 0 0;
}

.demand-detail-sheet {
  max-height: calc(100vh - 108px);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.detail-handle {
  width: 42px;
  height: 4px;
  margin: 0 auto 14px;
  background: #d3ded7;
  border-radius: 999px;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 12px;

  > div:nth-child(2) {
    min-width: 0;
    flex: 1;
  }

  span,
  strong {
    display: block;
  }

  span {
    color: #267062;
    font-size: 12px;
    font-weight: 850;
    text-transform: uppercase;
  }

  strong {
    margin-top: 2px;
    color: #111827;
    font-size: 20px;
    line-height: 1.15;
  }
}

.close-button {
  background: #f5f7f5;
  border: 1px solid #dfe7df;
}

.detail-status {
  width: auto;
  max-width: max-content;
  height: 22px !important;
  margin-top: 12px;
  padding: 0 10px !important;
  font-size: 11px !important;
  font-weight: 750;
}

.detail-description {
  margin: 14px 0 0;
  color: #46544c;
  font-size: 15px;
  line-height: 1.45;
}

.detail-list {
  margin-top: 16px;
  display: grid;
  gap: 10px;
}

.demand-form {
  margin-top: 18px;
  display: grid;
  gap: 12px;
}

.address-preview {
  padding: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #3f5148;
  background: #f7faf8;
  border: 1px solid #dfe9e2;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.35;
}

.save-demand-button {
  height: 50px !important;
  border-radius: 14px;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: none;
}

.demand-form-sheet {
  max-height: 88vh;
  overflow-y: auto;
}

.detail-row {
  padding: 12px;
  background: #f7faf8;
  border: 1px solid #e1e8e2;
  border-radius: 14px;

  span,
  strong {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 12px;
    font-weight: 800;
  }

  strong {
    margin-top: 3px;
    color: #111827;
    font-size: 14px;
    line-height: 1.25;
  }
}

.demand-thread {
  margin-top: 18px;
  padding-top: 16px;
  min-height: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  border-top: 1px solid #e1e8e2;
}

.thread-heading {
  strong,
  span {
    display: block;
  }

  strong {
    color: #111827;
    font-size: 17px;
    line-height: 1.2;
  }

  span {
    margin-top: 4px;
    color: #66736b;
    font-size: 13px;
    line-height: 1.35;
  }
}

.thread-list {
  margin: 12px 0 22px;
  display: grid;
  gap: 10px;
}

.demand-detail-sheet .thread-list {
  padding-bottom: 78px;
}

.thread-item {
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 10px;
  padding: 12px;
  background: #f7faf8;
  border: 1px solid #dfe9e2;
  border-radius: 14px;
}

.thread-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #007f3d;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 900;
}

.thread-content {
  min-width: 0;

  p {
    margin: 7px 0 0;
    color: #111827;
    font-size: 14px;
    line-height: 1.45;
    white-space: pre-wrap;
  }
}

.thread-meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;

  strong {
    color: #111827;
    font-size: 13px;
  }

  span {
    color: #66736b;
    font-size: 12px;
  }
}

.thread-attachment {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #007f3d;
  font-size: 13px;
  font-weight: 850;
  text-decoration: none;
}

.thread-empty {
  padding: 13px;
  color: #66736b;
  background: #f7faf8;
  border: 1px dashed #d4dfd7;
  border-radius: 14px;
  font-size: 13px;
}

.reply-button {
  height: 48px !important;
  margin-top: 12px;
  border-radius: 14px;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: none;
}

.demand-reply-footer {
  margin: 0 -18px -22px;
  padding: 12px 18px calc(14px + env(safe-area-inset-bottom));
  position: sticky;
  bottom: 0;
  z-index: 2;
  background: #ffffff;
  border-top: 1px solid #e1e8e2;
  box-shadow: 0 -10px 24px rgba(17, 24, 39, 0.06);
}

@media (max-width: 420px) {
  .list-card {
    align-items: flex-start;
  }

  .list-actions {
    gap: 6px;
  }

  .create-button {
    min-width: 0 !important;

    .v-icon {
      margin-right: 2px !important;
    }
  }

  .thread-meta {
    flex-direction: column;
    gap: 2px;
  }
}
</style>
